import React, { useState } from 'react';
import Icon from '../AppIcon';

const ResultsNavigation = ({ 
  onExport,
  onEmail,
  onPrint,
  onRetake,
  onScheduleFollowup,
  resultsData = null,
  isLoading = false 
}) => {
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [showExportOptions, setShowExportOptions] = useState(false);

  const handleExport = (format = 'pdf') => {
    if (onExport && !isLoading) {
      onExport(format);
    }
    setShowExportOptions(false);
  };

  const handleEmail = async () => {
    if (emailAddress && onEmail && !isLoading) {
      try {
        await onEmail(emailAddress);
        setEmailSent(true);
        setTimeout(() => {
          setShowEmailModal(false);
          setEmailSent(false);
          setEmailAddress('');
        }, 2000);
      } catch (error) {
        console.error('Failed to send email:', error);
      }
    }
  };

  const handlePrint = () => {
    if (onPrint && !isLoading) {
      onPrint();
    }
  };

  const handleRetake = () => {
    if (onRetake && !isLoading) {
      onRetake();
    }
  };

  const handleScheduleFollowup = () => {
    if (onScheduleFollowup && !isLoading) {
      onScheduleFollowup();
    }
  };

  return (
    <>
      {/* Results Action Bar */}
      <div className="bg-surface border border-border rounded-xl shadow-therapeutic p-6 space-y-6">
        <div className="text-center">
          <h3 className="text-lg font-heading font-semibold text-text-primary mb-2">
            Your Assessment is Complete
          </h3>
          <p className="text-text-secondary">
            Choose how you'd like to save or share your results
          </p>
        </div>

        {/* Primary Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Export Options */}
          <div className="relative">
            <button
              onClick={() => setShowExportOptions(!showExportOptions)}
              disabled={isLoading}
              className="w-full flex items-center justify-center space-x-3 px-6 py-4 bg-primary text-white rounded-lg font-medium hover:bg-primary-700 transition-all duration-200 disabled:opacity-50"
            >
              <Icon name="Download" size={20} />
              <span>Download Results</span>
              <Icon name="ChevronDown" size={16} />
            </button>

            {showExportOptions && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-surface border border-border rounded-lg shadow-elevated z-10">
                <button
                  onClick={() => handleExport('pdf')}
                  className="w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-surface-secondary transition-colors duration-150"
                >
                  <Icon name="FileText" size={16} />
                  <span className="text-sm">PDF Report</span>
                </button>
                <button
                  onClick={() => handleExport('csv')}
                  className="w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-surface-secondary transition-colors duration-150"
                >
                  <Icon name="Table" size={16} />
                  <span className="text-sm">Data Export (CSV)</span>
                </button>
              </div>
            )}
          </div>

          {/* Email Results */}
          <button
            onClick={() => setShowEmailModal(true)}
            disabled={isLoading}
            className="w-full flex items-center justify-center space-x-3 px-6 py-4 bg-secondary text-white rounded-lg font-medium hover:bg-secondary-700 transition-all duration-200 disabled:opacity-50"
          >
            <Icon name="Mail" size={20} />
            <span>Email Results</span>
          </button>
        </div>

        {/* Secondary Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <button
            onClick={handlePrint}
            disabled={isLoading}
            className="flex items-center justify-center space-x-2 px-4 py-3 bg-surface-secondary border border-border rounded-lg text-text-primary hover:bg-border-light transition-all duration-150 disabled:opacity-50"
          >
            <Icon name="Printer" size={16} />
            <span className="text-sm font-medium">Print</span>
          </button>

          <button
            onClick={handleScheduleFollowup}
            disabled={isLoading}
            className="flex items-center justify-center space-x-2 px-4 py-3 bg-accent-50 border border-accent-100 text-accent-600 rounded-lg hover:bg-accent-100 transition-all duration-150 disabled:opacity-50"
          >
            <Icon name="Calendar" size={16} />
            <span className="text-sm font-medium">Schedule Follow-up</span>
          </button>

          <button
            onClick={handleRetake}
            disabled={isLoading}
            className="flex items-center justify-center space-x-2 px-4 py-3 bg-surface border border-border rounded-lg text-text-primary hover:bg-surface-secondary transition-all duration-150 disabled:opacity-50"
          >
            <Icon name="RotateCcw" size={16} />
            <span className="text-sm font-medium">Retake Assessment</span>
          </button>
        </div>

        {/* Results Summary */}
        {resultsData && (
          <div className="bg-surface-secondary rounded-lg p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-secondary">Assessment completed:</span>
              <span className="font-medium text-text-primary">
                {new Date().toLocaleDateString()}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm mt-1">
              <span className="text-text-secondary">Total questions answered:</span>
              <span className="font-medium text-text-primary">
                {resultsData.totalQuestions || 'N/A'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-70 flex items-center justify-center p-4">
          <div className="bg-surface rounded-xl shadow-modal border border-border max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-heading font-semibold text-text-primary">
                  Email Results
                </h3>
                <button
                  onClick={() => setShowEmailModal(false)}
                  className="p-1 text-text-secondary hover:text-text-primary transition-colors duration-150"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>

              {emailSent ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Check" size={24} color="#10B981" />
                  </div>
                  <h4 className="font-medium text-text-primary mb-2">Email Sent!</h4>
                  <p className="text-sm text-text-secondary">
                    Your results have been sent to {emailAddress}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={emailAddress}
                      onChange={(e) => setEmailAddress(e.target.value)}
                      placeholder="Enter your email address"
                      className="form-input"
                    />
                  </div>

                  <div className="bg-primary-50 border border-primary-100 rounded-lg p-3">
                    <div className="flex items-start space-x-2">
                      <Icon name="Info" size={16} color="#2563EB" className="mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-primary-600">
                        Your results will be sent as a secure, encrypted PDF attachment.
                      </p>
                    </div>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      onClick={() => setShowEmailModal(false)}
                      className="flex-1 px-4 py-2 bg-surface border border-border rounded-lg text-text-primary font-medium hover:bg-surface-secondary transition-all duration-150"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleEmail}
                      disabled={!emailAddress || isLoading}
                      className="flex-1 px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-700 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? 'Sending...' : 'Send Email'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ResultsNavigation;